import { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Upload, Eye, EyeOff, Pencil } from 'lucide-react';
import { apiClient } from '@/api/client';

interface Sticker {
  id: string;
  packId: string;
  url: string;
  isAnimated: boolean;
  mimeType: string;
  order: number;
}
interface StickerPack {
  id: string;
  name: string;
  authorId: string;
  isPublic: boolean;
  coverUrl: string | null;
  order: number;
  createdAt: string;
  stickers?: Sticker[];
  _count?: { stickers: number };
}

const apiBase = ((import.meta as any).env?.VITE_API_URL ?? '').replace(/\/$/, '');

function toSrc(url: string): string {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return `${apiBase}${url}`;
  return url;
}

export default function AdminStickers() {
  const [packs, setPacks] = useState<StickerPack[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedPack, setSelectedPack] = useState<StickerPack | null>(null);
  const [newPackName, setNewPackName] = useState('');
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadPacks = async () => {
    setLoading(true);
    try {
      // admin needs ALL packs, but public endpoint returns only public ones.
      // Workaround: fetch public ones + current admin's private packs via /me.
      const [pub, mine] = await Promise.all([
        apiClient.get<StickerPack[]>('/api/stickers/packs'),
        apiClient.get<StickerPack[]>('/api/stickers/me').catch(() => ({ data: [] as StickerPack[] })),
      ]);
      const map = new Map<string, StickerPack>();
      pub.data.forEach((p) => map.set(p.id, p));
      (mine.data ?? []).forEach((p) => map.set(p.id, p));
      setPacks([...map.values()].sort((a, b) => a.order - b.order));
    } finally {
      setLoading(false);
    }
  };

  const loadPack = async (id: string) => {
    const res = await apiClient.get<StickerPack>(`/api/stickers/packs/${id}`);
    setSelectedPack(res.data);
  };

  useEffect(() => { loadPacks(); }, []);
  useEffect(() => { if (selectedId) loadPack(selectedId); }, [selectedId]);

  const createPack = async () => {
    if (!newPackName.trim()) return;
    const res = await apiClient.post<StickerPack>('/api/stickers/packs', {
      name: newPackName.trim(),
      isPublic: false,
    });
    setNewPackName('');
    await loadPacks();
    setSelectedId(res.data.id);
  };

  const togglePublic = async (pack: StickerPack) => {
    await apiClient.put(`/api/stickers/packs/${pack.id}`, { isPublic: !pack.isPublic });
    await loadPacks();
    if (selectedId === pack.id) await loadPack(pack.id);
  };

  const renamePack = async (pack: StickerPack) => {
    const next = prompt('Новое название', pack.name);
    if (!next || next === pack.name) return;
    await apiClient.put(`/api/stickers/packs/${pack.id}`, { name: next });
    await loadPacks();
    if (selectedId === pack.id) await loadPack(pack.id);
  };

  const deletePack = async (pack: StickerPack) => {
    if (!confirm(`Удалить пак "${pack.name}" вместе со всеми стикерами?`)) return;
    await apiClient.delete(`/api/stickers/packs/${pack.id}`);
    if (selectedId === pack.id) { setSelectedId(null); setSelectedPack(null); }
    await loadPacks();
  };

  const uploadSticker = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !selectedId) return;
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', file);
        await apiClient.post(`/api/stickers/packs/${selectedId}/sticker`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
    } catch (err: any) {
      alert(err.response?.data?.message ?? 'Ошибка загрузки');
    } finally {
      e.target.value = '';
      await loadPack(selectedId);
    }
  };

  const deleteSticker = async (st: Sticker) => {
    if (!confirm('Удалить стикер?')) return;
    await apiClient.delete(`/api/stickers/${st.id}`);
    if (selectedId) await loadPack(selectedId);
  };

  return (
    <div className="p-4 max-w-6xl">
      <h2 className="text-xl font-semibold text-surface-100 mb-4">Стикер-паки</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Packs list */}
        <aside className="md:col-span-1 bg-surface-900 border border-surface-800 rounded-lg p-3 space-y-2 h-fit">
          <div className="flex gap-2">
            <input
              value={newPackName}
              onChange={(e) => setNewPackName(e.target.value)}
              placeholder="Название пака"
              className="flex-1 bg-surface-800 border border-surface-700 rounded px-2 py-1 text-sm text-surface-100"
            />
            <button onClick={createPack} className="btn-primary text-xs flex items-center gap-1">
              <Plus size={12} /> Создать
            </button>
          </div>

          {loading ? (
            <p className="text-surface-500 text-sm">Загрузка…</p>
          ) : packs.length === 0 ? (
            <p className="text-surface-500 text-sm">Паков нет — создайте первый.</p>
          ) : (
            <ul className="space-y-1">
              {packs.map((p) => (
                <li
                  key={p.id}
                  className={`flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer ${selectedId === p.id ? 'bg-brand-600/30 border border-brand-600/60' : 'bg-surface-800 hover:bg-surface-700'}`}
                  onClick={() => setSelectedId(p.id)}
                >
                  <span className="flex-1 text-sm text-surface-100 truncate">
                    {p.name}{' '}
                    <span className="text-surface-500 text-xs">({p._count?.stickers ?? p.stickers?.length ?? 0})</span>
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); togglePublic(p); }}
                    title={p.isPublic ? 'Публичный' : 'Приватный'}
                    className={p.isPublic ? 'text-emerald-400' : 'text-surface-500 hover:text-surface-300'}
                  >
                    {p.isPublic ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); renamePack(p); }}
                    className="text-surface-500 hover:text-brand-400"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deletePack(p); }}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Selected pack detail */}
        <section className="md:col-span-2 bg-surface-900 border border-surface-800 rounded-lg p-4 min-h-[400px]">
          {!selectedPack ? (
            <p className="text-surface-500 text-sm">Выберите пак слева, чтобы загрузить стикеры.</p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-surface-100 font-semibold">{selectedPack.name}</h3>
                  <p className="text-xs text-surface-500">
                    {selectedPack.isPublic ? 'Публичный' : 'Приватный'} · {selectedPack.stickers?.length ?? 0} стикеров
                  </p>
                </div>
                <button onClick={() => fileInputRef.current?.click()} className="btn-primary text-sm flex items-center gap-1.5">
                  <Upload size={14} /> Загрузить стикер
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/webp,image/gif,image/png,image/jpeg,application/json"
                  multiple
                  onChange={uploadSticker}
                  className="hidden"
                />
              </div>

              <p className="text-xs text-surface-500 mb-3">
                Поддержка: WebP (анимированный OK), GIF, PNG, JPG, Lottie (.tgs/.json). Макс 1 МБ на стикер.
              </p>

              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                {(selectedPack.stickers ?? []).map((st) => (
                  <div key={st.id} className="relative group bg-surface-800 border border-surface-700 rounded-lg p-2 flex items-center justify-center aspect-square">
                    {st.mimeType === 'application/json' ? (
                      <span className="text-[10px] text-surface-400 text-center">Lottie<br/>{st.id.slice(-6)}</span>
                    ) : (
                      <img src={toSrc(st.url)} alt="" className="max-w-full max-h-full object-contain" />
                    )}
                    <button
                      onClick={() => deleteSticker(st)}
                      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-1 rounded-full bg-red-600 hover:bg-red-500 transition-opacity"
                      title="Удалить"
                    >
                      <Trash2 size={10} className="text-white" />
                    </button>
                    {st.isAnimated && (
                      <span className="absolute bottom-1 left-1 text-[9px] bg-black/60 text-white px-1 rounded">anim</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
